import { useState } from 'react';

import PropTypes from 'prop-types';

import Button from '#globalComponents/Button/Button';
import { ConfirmModal } from '#globalComponents/Overlay/ConfirmModal';
import Tag from '#globalComponents/Tag/Tag';
import { useAuth } from '#globalContexts/AuthProvider';
import { isAddonManageable } from '#lib/addons/addonConfigs';
import { useAddonTrial } from '#lib/addons/useAddonTrial';
import { formatDate } from '#lib/dateUtils';
import { useStartAddon, useCancelAddon } from '#settingsFeature/models/billing/mutations';

import AddonPurchaseConfirmModal from './AddonPurchaseConfirmModal';

/** Derives the card's view state from the trial/add-on flags. */
function resolveAddonView({ cancelAtPeriodEnd, addonTrialActive, addonActive, trialUsed, hasTrial }) {
	if (cancelAtPeriodEnd) return 'cancelPending';
	if (addonTrialActive) return 'trial';
	if (addonActive) return 'active';
	if (!hasTrial) return 'buy';
	return trialUsed ? 'activate' : 'start';
}

/**
 * Generic add-on management row for billing settings. Shows start / trial / active /
 * cancel-pending / activate states; hidden where `isAddonManageable` says so. Driven by an add-on config.
 */
const AddonCard = ({ addon }) => {
	const { orgPlan } = useAuth();
	const {
		isOnTrial,
		trialSource,
		addonActive,
		daysRemaining,
		trialEndsAt,
		accessEndsAt,
		cancelAtPeriodEnd,
		trialUsed,
		hasTrial,
	} = useAddonTrial(addon.key);
	const startAddon = useStartAddon();
	const cancelAddon = useCancelAddon();
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [purchaseConfirmOpen, setPurchaseConfirmOpen] = useState(false);
	const Icon = addon.icon;

	if (!isAddonManageable(addon, orgPlan)) return null;

	const addonTrialActive = isOnTrial && trialSource === 'addon';
	const view = resolveAddonView({ cancelAtPeriodEnd, addonTrialActive, addonActive, trialUsed, hasTrial });
	const daysText = daysRemaining === 1 ? '1 Tag' : `${daysRemaining ?? 0} Tage`;
	const endDate = trialEndsAt ? formatDate(trialEndsAt) : null;
	const accessEndDate = accessEndsAt ? formatDate(accessEndsAt) : null;

	const onStart = () => startAddon.mutate(addon.key);
	const onConfirmCancel = () => cancelAddon.mutate(addon.key);
	const onConfirmPurchase = () => startAddon.mutateAsync(addon.key);

	const BODY = {
		cancelPending: `Gekündigt – Ihr ${addon.name}-Zugriff endet ${accessEndDate ? `am ${accessEndDate}` : 'zum Periodenende'}. Ihre Daten bleiben erhalten.`,
		trial: `Testphase aktiv – noch ${daysText}.${endDate ? ` Wird am ${endDate} kostenpflichtig (${addon.priceLabel}).` : ''}`,
		active: `${addon.name} Add-on aktiv – ${addon.priceLabel}.`,
		start: `${addon.trialDays} Tage kostenlos testen, danach ${addon.priceLabel}. Jederzeit kündbar.`,
		activate: `Ihre Testphase ist beendet. ${addon.name}-Add-on aktivieren für ${addon.priceLabel}. Jederzeit kündbar.`,
		buy: `${addon.name}-Add-on für ${addon.priceLabel}. Jederzeit kündbar.`,
	};

	const startBtn = (label, onClick = onStart) => (
		<Button size="sm" onClick={onClick} loading={startAddon.isPending}>
			{label}
		</Button>
	);
	const purchaseBtn = startBtn(`${addon.name}-Add-on aktivieren`, () => setPurchaseConfirmOpen(true));
	const cancelBtn = (
		<Button size="sm" variant="outline" onClick={() => setConfirmOpen(true)} loading={cancelAddon.isPending}>
			Add-on kündigen
		</Button>
	);

	const ACTION = {
		cancelPending: startBtn('Kündigung rückgängig machen'),
		trial: cancelBtn,
		active: cancelBtn,
		start: startBtn(`${addon.trialDays} Tage kostenlos testen`),
		activate: purchaseBtn,
		buy: purchaseBtn,
	};

	const badge =
		(addonActive || addonTrialActive) && !cancelAtPeriodEnd ? (addonTrialActive ? 'Testphase' : 'Aktiv') : null;

	return (
		<div className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center">
			<div className="flex min-w-0 flex-1 items-start gap-3">
				<div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-control bg-primary-100 text-primary-600">
					<Icon className="h-4 w-4" />
				</div>
				<div className="min-w-0">
					<div className="flex items-center gap-2">
						<h3 className="text-sm font-semibold text-text">{addon.name} Add-on</h3>
						{badge && <Tag tone="success" label={badge} />}
					</div>
					<p className="mt-0.5 text-sm text-bgBase-800">{BODY[view]}</p>
				</div>
			</div>
			<div className="flex-shrink-0 sm:ml-auto">{ACTION[view]}</div>

			<ConfirmModal
				isOpen={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				onConfirm={onConfirmCancel}
				title={`${addon.name}-Add-on kündigen`}
				message={`Möchten Sie das ${addon.name}-Add-on wirklich kündigen?`}
				subtitle="Die Kündigung erfolgt zum Ende der aktuellen Abrechnungsperiode. Ihre Daten bleiben erhalten."
				confirmText="Add-on kündigen"
				cancelText="Behalten"
				isLoading={cancelAddon.isPending}
			/>
			<AddonPurchaseConfirmModal
				addon={addon}
				isOpen={purchaseConfirmOpen}
				onClose={() => setPurchaseConfirmOpen(false)}
				onConfirm={onConfirmPurchase}
				isLoading={startAddon.isPending}
			/>
		</div>
	);
};

AddonCard.propTypes = {
	addon: PropTypes.shape({
		key: PropTypes.string.isRequired,
		name: PropTypes.string.isRequired,
		priceLabel: PropTypes.string.isRequired,
		trialDays: PropTypes.number.isRequired,
		proComp: PropTypes.bool,
		icon: PropTypes.elementType.isRequired,
	}).isRequired,
};

export default AddonCard;
